<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="false"
                title="Hotel Bill Statement"
                @onClickCloseButton="closeButton"
            />
        </div>

        <div v-if="hasGuest" class="row p-1 px-2">
            <div class="col-md-6">
                <p>Guest Name: {{ statement.patient.full_name }}</p>
                <p>Guest ID: {{ statement.patient.patient_id }}</p>
                <p class="capitalize">{{ ageSexLine }}</p>
                <p>Guest Phone No: {{ statement.patient.mobile_no }}</p>
                <p>Guest Address: {{ statement.patient.full_address }}</p>
            </div>
            <div class="col-md-6">
                <p>Reservation No: {{ statement.admission_no }}</p>
                <p>Check-in: {{ statement.admission_date }}</p>
                <p>Room: {{ roomName }}</p>
                <p>Booked By: {{ bookedByName }}</p>
                <p>Referrer: {{ referrerName }}</p>
            </div>
        </div>

        <div class="px-2">
            <ul class="nav nav-tabs">
                <li v-for="tab in tabs" :key="tab.id" class="nav-item">
                    <a
                        :class="['nav-link', { active: activeTab.id === tab.id }]"
                        @click="setActiveTab(tab)"
                    >
                        {{ tab.title }}
                    </a>
                </li>
            </ul>
        </div>

        <div class="col-12 px-2 mt-2 mb-5">
            <component
                :is="tabComponents[activeTab.id]"
                :service="service"
                :receiptGenerals="receiptGenerals"
                :total="total"
                :patientDetails="statement"
                :companyInfo="companyInfo"
                @onSettled="getStatement"
            />
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import HotelStatementTab from '@/components/molecule/company/hotel/hotel-bill-statement/tabs/HotelStatementTab.vue';
import HotelBillTab from '@/components/molecule/company/hotel/hotel-bill-statement/tabs/HotelBillTab.vue';
import HotelMoneyReceiptTab from '@/components/molecule/company/hotel/hotel-bill-statement/tabs/HotelMoneyReceiptTab.vue';
import Loader from '@/components/atom/LoaderComponent';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import handleHospital from '@/services/modules/hospital';
import handleCompany from '@/services/modules/company';
import handleHotel from '@/services/modules/hotel/hotel';

const $route = useRoute();
const $router = useRouter();
const showError = inject('showError');
const { fetchIpdStatement, calculateAge } = handleHospital();
const { fetchCompanyInfo } = handleCompany();
const { fetchSingleHotelReservation } = handleHotel();

const loading = ref(false);
const statement = ref({});
const service = ref([]);
const receiptGenerals = ref([]);
const total = ref({});
const companyInfo = ref({});
const rooms = ref([]);

const tabs = [
    { id: 'statement', title: 'Statements' },
    { id: 'ipd-bill', title: 'Bill' },
    { id: 'money-receipts', title: 'MoneyReceipts' },
];

const tabComponents = {
    'statement': HotelStatementTab,
    'ipd-bill': HotelBillTab,
    'money-receipts': HotelMoneyReceiptTab,
};

const activeTab = ref(tabs[0]);

const hasGuest = computed(() => {
    if (!statement.value.patient) return false;
    return true;
});

const ageSexLine = computed(() => {
    if (!statement.value.patient) return '';
    const birthday = statement.value.patient.birthday;
    const gender = statement.value.patient.gender;
    const agePart = birthday ? 'Age: ' + calculateAge(birthday) : '';
    const sexPart = gender ? ' | Sex: ' + gender : '';
    return agePart + sexPart;
});

const roomName = computed(() => {
    if (!rooms.value.length) return '';
    return rooms.value.map(room => room.name).join(', ');
});

const bookedByName = computed(() => {
    if (!statement.value.admitted_by_user) return '';
    return statement.value.admitted_by_user.name;
});

const referrerName = computed(() => {
    if (!statement.value.referrer) return '';
    return statement.value.referrer.name;
});

const getQuery = () => {
    const ipdRegisterId = $route.params.ipdRegisterId || $route.query.ipd_register_id;
    return `/${ipdRegisterId}?company_id=${$route.params.companyId}`;
};

const getStatement = () => {
    loading.value = true;
    Promise.all([
        fetchIpdStatement(getQuery()).then(res => {
            if (!res.status) return;
            statement.value = res.data;
            service.value = res.service_data;
            receiptGenerals.value = res.receipt_generals;
            total.value = res.total;
            total.value.settlement_discount = res.data.settlement_discount;
        }),
        fetchCompanyInfo($route.params.companyId).then(res => {
            companyInfo.value = res.data;
        }),
        getRooms(),
    ]).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const getRooms = () => {
    const ipdRegisterId = $route.params.ipdRegisterId || $route.query.ipd_register_id;
    const query = `?company_id=${$route.params.companyId}`;
    return fetchSingleHotelReservation(query, ipdRegisterId).then(res => {
        if (!res.status) return;
        if (!res.data.rooms) return;
        rooms.value = res.data.rooms;
    });
};

const setActiveTab = (tab) => {
    activeTab.value = tab;
    const routerObject = {
        path: $route.path,
        query: Object.assign({}, $route.query, { tab: tab.id }),
    };
    $router.replace(routerObject);
};

const restoreActiveTab = () => {
    const currentTab = $route.query.tab;
    if (!currentTab) return;
    const matched = tabs.find(tab => tab.id === currentTab);
    if (!matched) return;
    activeTab.value = matched;
};

const closeButton = () => {
    $router.back();
};

onMounted(() => {
    restoreActiveTab();
    getStatement();
});
</script>
